/**
 * Service Worker Registration Utility
 * Handles registration, updates, and communication with service worker
 */

/**
 * Register the service worker
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker() {
  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service Workers not supported in this browser');
    return null;
  }

  try {
    console.log('[SW] Registering Service Worker...');

    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });

    console.log('[SW] Service Worker registered successfully:', registration.scope);

    // Check for updates on page load
    registration.update();

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      console.log('[SW] New Service Worker found, installing...');

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker available
          console.log('[SW] New version available! Notify user to update.');

          // Dispatch custom event to notify app
          window.dispatchEvent(new CustomEvent('swUpdateAvailable', {
            detail: { registration }
          }));
        }
      });
    });

    // Listen for controller change (new SW activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[SW] Controller changed, reloading page...');
      window.location.reload();
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      console.log('[SW] Message from Service Worker:', event.data);

      if (event.data && event.data.type === 'SYNC_COMPLETE') {
        // Dispatch sync complete event
        window.dispatchEvent(new CustomEvent('swSyncComplete', {
          detail: event.data
        }));
      }

      if (event.data && event.data.type === 'OFFLINE_MUTATIONS_DISABLED') {
        window.dispatchEvent(new CustomEvent('swOfflineMutationsDisabled', {
          detail: event.data
        }));
      }
    });

    return registration;
  } catch (error) {
    console.error('[SW] Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Unregister all service workers (for debugging)
 * @returns {Promise<boolean>}
 */
export async function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      await registration.unregister();
      console.log('[SW] Service Worker unregistered');
    }

    return true;
  } catch (error) {
    console.error('[SW] Failed to unregister Service Worker:', error);
    return false;
  }
}

/**
 * Skip waiting and activate new service worker immediately
 * @param {ServiceWorkerRegistration} registration
 */
export function skipWaiting(registration) {
  if (!registration || !registration.waiting) {
    return;
  }

  console.log('[SW] Skipping waiting, activating new service worker...');

  // Tell the service worker to skip waiting
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Check if user is online
 * @returns {boolean}
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Add online/offline event listeners
 * @param {Function} onOnline - Callback when going online
 * @param {Function} onOffline - Callback when going offline
 * @returns {Function} Cleanup function to remove listeners
 */
export function watchConnectionStatus(onOnline, onOffline) {
  const handleOnline = () => {
    console.log('[SW] Connection status: ONLINE');
    if (onOnline) onOnline();
  };

  const handleOffline = () => {
    console.log('[SW] Connection status: OFFLINE');
    if (onOffline) onOffline();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

/**
 * Request persistent storage (to prevent cache eviction)
 * @returns {Promise<boolean>}
 */
export async function requestPersistentStorage() {
  if (!navigator.storage || !navigator.storage.persist) {
    console.log('[SW] Persistent storage not supported');
    return false;
  }

  try {
    const isPersisted = await navigator.storage.persist();
    console.log('[SW] Persistent storage:', isPersisted ? 'GRANTED' : 'DENIED');
    return isPersisted;
  } catch (error) {
    console.error('[SW] Failed to request persistent storage:', error);
    return false;
  }
}

/**
 * Get storage estimate
 * @returns {Promise<{usage: number, quota: number, percentage: number}|null>}
 */
export async function getStorageEstimate() {
  if (!navigator.storage || !navigator.storage.estimate) {
    console.log('[SW] Storage estimate not supported');
    return null;
  }

  try {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage || 0;
    const quota = estimate.quota || 0;
    const percentage = quota > 0 ? ((usage / quota) * 100).toFixed(2) : 0;

    console.log('[SW] Storage usage:', {
      usage: `${(usage / 1024 / 1024).toFixed(2)} MB`,
      quota: `${(quota / 1024 / 1024).toFixed(2)} MB`,
      percentage: `${percentage}%`
    });

    return { usage, quota, percentage };
  } catch (error) {
    console.error('[SW] Failed to get storage estimate:', error);
    return null;
  }
}

/**
 * Clear all caches
 * @returns {Promise<boolean>}
 */
export async function clearAllCaches() {
  try {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log('[SW] All caches cleared');
    return true;
  } catch (error) {
    console.error('[SW] Failed to clear caches:', error);
    return false;
  }
}

/**
 * Pre-cache specific URLs
 * @param {string[]} urls - Array of URLs to cache
 * @returns {Promise<boolean>}
 */
export async function precacheUrls(urls) {
  if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
    console.warn('[SW] No active service worker, cannot precache');
    return false;
  }

  try {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_URLS',
      urls
    });
    console.log('[SW] Requested precaching of', urls.length, 'URLs');
    return true;
  } catch (error) {
    console.error('[SW] Failed to precache URLs:', error);
    return false;
  }
}

export default {
  registerServiceWorker,
  unregisterServiceWorker,
  skipWaiting,
  isOnline,
  watchConnectionStatus,
  requestPersistentStorage,
  getStorageEstimate,
  clearAllCaches,
  precacheUrls
};
